<template>
  <v-dialog
    v-model="isOpen"
    max-width="1150"
    persistent
    scrollable
  >
    <v-card class="guarantee-config-dialog">
      <v-card-title class="guarantee-config-dialog__header">
        <div>
          <div class="text-h6 font-weight-bold">Garantías</div>
          <div class="text-body-2">
            Configuración por negocio o por sucursal
          </div>
        </div>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <block v-if="loading" />

        <template v-else>
          <v-tabs v-model="activeTab" background-color="transparent" grow>
            <v-tab v-for="type in guaranteeTypes" :key="type">
              {{ typeLabels[type] }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <v-tab-item
              v-for="type in guaranteeTypes"
              :key="type"
            >
              <div class="pt-4">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="stateByType[type].isActive"
                      inset
                      color="success"
                      :label="stateByType[type].isActive ? 'Activa' : 'Inactiva'"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="stateByType[type].applyAll"
                      inset
                      color="primary"
                      label="Aplicar a todas las sucursales"
                      @change="handleScopeChange(type)"
                    />
                  </v-col>

                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="stateByType[type].copySource"
                      :items="copyOptions(type)"
                      item-text="label"
                      item-value="value"
                      label="Copiar de"
                      outlined
                      dense
                      attach
                      clearable
                      @change="copyFromSource(type)"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6" v-if="!stateByType[type].applyAll">
                    <v-autocomplete
                      v-model="stateByType[type].branchId"
                      :items="branchOptions"
                      item-text="branchLabel"
                      item-value="ID_Branch"
                      label="Sucursal"
                      outlined
                      dense
                      attach
                      :rules="[value => !!value || 'Seleccione una sucursal']"
                      @change="loadScopeContent(type)"
                    />
                  </v-col>

                </v-row>

                <div class="guarantee-editor mb-4">
                  <quill-editor
                    :key="stateByType[type].editorKey"
                    v-model="stateByType[type].content"
                    :options="quillOptions"
                  />
                </div>

                <div class="d-flex justify-end align-center">
                  <v-btn text class="mr-2" @click="resetTab(type)">
                    Limpiar
                  </v-btn>
                  <v-btn
                    class="btn-save"
                    :loading="savingType === type"
                    @click="saveTab(type)"
                  >
                    Guardar
                  </v-btn>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </v-card-text>
    </v-card>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      top
      right
      timeout="3000"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import Block from "@/components/Block";

const GUARANTEE_TYPES = ["ABONO", "TICKET", "COMPROBANTE"];

function createTypeState() {
  return {
    id: null,
    isActive: true,
    applyAll: true,
    branchId: null,
    content: "",
    copySource: null,
    editorKey: 0,
    lastLoadedContent: "",
  };
}

export default {
  name: "GuaranteeConfiguratorDialog",
  components: { Block },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    business: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    savingType: null,
    activeTab: 0,
    guaranteeTypes: GUARANTEE_TYPES,
    typeLabels: {
      ABONO: "Abonos",
      TICKET: "Tickets",
      COMPROBANTE: "Comprobantes",
    },
    branchOptions: [],
    allRecords: [],
    stateByType: {
      ABONO: createTypeState(),
      TICKET: createTypeState(),
      COMPROBANTE: createTypeState(),
    },
    snackbar: {
      show: false,
      color: "success",
      text: "",
    },
    quillOptions: {
      formats: ["bold", "italic", "list", "align"],
      modules: {
        toolbar: [
          ["bold", "italic"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["clean"],
        ],
      },
      placeholder: "Ingrese el texto de la garantía",
    },
  }),
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(nextValue) {
        this.$emit("input", nextValue);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(nextValue) {
        if (nextValue) {
          this.loadData();
        }
      },
    },
  },
  methods: {
    close() {
      this.isOpen = false;
      this.$emit("close");
    },
    showMessage(color, text) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.show = true;
    },
    branchLabel(branch) {
      if (!branch) return "General";
      return branch.BranchName || branch.BranchLegalName || `Sucursal ${branch.ID_Branch}`;
    },
    scopeLabel(record) {
      return record.ID_Branch ? `Sucursal: ${this.branchLabel(record)}` : "General";
    },
    buildRecordKey(type, record) {
      const branchKey = record.ID_Branch ? `branch-${record.ID_Branch}` : "business";
      return `${type}-${branchKey}`;
    },
    normalizeRecord(record) {
      return {
        ...record,
        ID_Branch: record.ID_Branch === null ? null : Number(record.ID_Branch),
        IsActive: Number(record.IsActive) === 1,
      };
    },
    async loadData() {
      if (!this.business || !this.business.ID_Business) {
        return;
      }

      this.loading = true;

      try {
        const [guaranteesResponse, branchesResponse] = await Promise.all([
          this.$API.businessGuarantees.list(this.business.ID_Business),
          this.$API.branches.findBranches(this.business.ID_Business),
        ]);

        this.allRecords = Array.isArray(guaranteesResponse?.data)
          ? guaranteesResponse.data.map((record) => this.normalizeRecord(record))
          : [];

        this.branchOptions = Array.isArray(branchesResponse)
          ? branchesResponse.map((branch) => ({
              ...branch,
              branchLabel: branch.BranchName || branch.BranchLegalName || `Sucursal ${branch.ID_Branch}`,
            }))
          : [];

        this.activeTab = 0;
        this.guaranteeTypes.forEach((type) => {
          this.loadScopeContent(type, true);
        });
      } catch (error) {
        this.showMessage("error", "No fue posible cargar las garantías.");
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },
    recordsByType(type) {
      return this.allRecords.filter((record) => record.GuaranteeType === type);
    },
    currentRecord(type) {
      const state = this.stateByType[type];
      if (state.applyAll) {
        return this.recordsByType(type).find((record) => record.ID_Branch === null) || null;
      }

      if (!state.branchId) {
        return null;
      }

      return (
        this.recordsByType(type).find(
          (record) => Number(record.ID_Branch) === Number(state.branchId)
        ) || null
      );
    },
    loadScopeContent(type, preserveEditorKey = false) {
      const state = this.stateByType[type];
      const record = this.currentRecord(type);

      state.id = record ? record.ID_BusinessGuarantee : null;
      state.isActive = record ? Boolean(record.IsActive) : true;
      state.content = record ? record.ContentHtml || "" : "";
      state.lastLoadedContent = state.content;
      state.copySource = null;

      if (!preserveEditorKey) {
        state.editorKey += 1;
      }
    },
    handleScopeChange(type) {
      const state = this.stateByType[type];
      if (state.applyAll) {
        state.branchId = null;
      }
      this.loadScopeContent(type);
    },
    copyOptions(type) {
      const currentState = this.stateByType[type];
      const currentKey = this.buildRecordKey(type, {
        ID_Branch: currentState.applyAll ? null : currentState.branchId,
      });

      return this.allRecords
        .filter((record) => this.buildRecordKey(record.GuaranteeType, record) !== currentKey)
        .map((record) => ({
          value: this.buildRecordKey(record.GuaranteeType, record),
          label: `${this.typeLabels[record.GuaranteeType] || record.GuaranteeType} - ${this.scopeLabel(record)}`,
          record,
        }));
    },
    copyFromSource(type) {
      const state = this.stateByType[type];
      if (!state.copySource) {
        return;
      }

      const source = this.allRecords.find(
        (record) => this.buildRecordKey(record.GuaranteeType, record) === state.copySource
      );

      state.copySource = null;

      if (!source) {
        this.showMessage("warning", "No se encontró la garantía origen seleccionada.");
        return;
      }

      if (state.content && state.content !== state.lastLoadedContent) {
        const confirmed = window.confirm(
          "El contenido actual será reemplazado por la garantía seleccionada. ¿Desea continuar?"
        );
        if (!confirmed) {
          return;
        }
      }

      state.content = source.ContentHtml || "";
      state.editorKey += 1;
    },
    resetTab(type) {
      const state = this.stateByType[type];
      state.content = "";
      state.isActive = true;
      state.copySource = null;
      state.editorKey += 1;
    },
    async saveTab(type) {
      const state = this.stateByType[type];

      if (!state.applyAll && !state.branchId) {
        this.showMessage("warning", "Debe seleccionar una sucursal.");
        return;
      }

      this.savingType = type;

      try {
        const payload = {
          ID_Business: this.business.ID_Business,
          ID_Branch: state.applyAll ? null : state.branchId,
          GuaranteeType: type,
          ContentHtml: state.content,
          IsActive: state.isActive ? 1 : 0,
        };

        const response = await this.$API.businessGuarantees.save(payload);
        const savedRecord = this.normalizeRecord(response?.data || {});

        this.upsertRecord(savedRecord);
        this.$API.businessGuarantees.updateEffectiveCacheFromRecord(savedRecord);
        this.loadScopeContent(type, true);
        this.showMessage("success", "Garantía guardada correctamente.");
        this.$emit("saved", {
          type,
          record: savedRecord,
        });
      } catch (error) {
        this.showMessage("error", "No fue posible guardar la garantía.");
        this.$emit("error", error);
      } finally {
        this.savingType = null;
      }
    },
    upsertRecord(record) {
      const index = this.allRecords.findIndex(
        (item) =>
          item.ID_BusinessGuarantee === record.ID_BusinessGuarantee ||
          (
            item.GuaranteeType === record.GuaranteeType &&
            Number(item.ID_Branch || 0) === Number(record.ID_Branch || 0)
          )
      );

      if (index >= 0) {
        this.$set(this.allRecords, index, record);
        return;
      }

      this.allRecords.push(record);
    },
  },
};
</script>

<style scoped>
.guarantee-config-dialog__header {
  align-items: flex-start;
  gap: 16px;
}

.guarantee-editor {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

::v-deep .ql-toolbar.ql-snow {
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

::v-deep .ql-container.ql-snow {
  border: none;
}

::v-deep .ql-editor {
  min-height: 280px;
  font-size: 14px;
}
</style>
